import * as React from 'react'

import { cn } from '../cn'
import { Checkbox } from './checkbox'
import { Input } from './input'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from './table'

// ---------------------------------------------------------------------------
// Money helpers — self-contained so the primitive carries no app imports.
// Values are integer cents; inputs are plain "4.50"-style strings.
// ---------------------------------------------------------------------------

const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/** "$4.50" from 450. */
export function formatPriceCents(cents: number): string {
  return usdFormatter.format(cents / 100)
}

/** 450 → "4.50"; null → "" (unpriced). */
export function centsToPriceInput(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2)
}

/** "4.50" → 450; empty or malformed → null. */
export function priceInputToCents(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) {
    return null
  }
  return Math.round(Number(trimmed) * 100)
}

/** Non-empty input that does not parse as money. Empty is valid (= unpriced). */
export function priceInputInvalid(value: string): boolean {
  return value.trim().length > 0 && priceInputToCents(value) === null
}

// ---------------------------------------------------------------------------
// Source badge — provenance chip for a resolved price/cost value.
// ---------------------------------------------------------------------------

export type PricingMatrixSourceTone = 'gray' | 'blue' | 'purple' | 'amber' | 'slate'

const sourceToneClasses: Record<PricingMatrixSourceTone, string> = {
  gray: 'border-transparent bg-secondary text-secondary-foreground',
  blue: 'border-transparent bg-blue-500/10 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  purple: 'border-transparent bg-purple-500/10 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
  amber: 'border-transparent bg-amber-500/10 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  slate: 'border-border text-muted-foreground',
}

export interface PricingSourceBadgeProps extends React.HTMLAttributes<HTMLElement> {
  label: string
  tone?: PricingMatrixSourceTone
  /** Render as a focusable button — needed when the badge triggers a popover or explain action. */
  interactive?: boolean
}

/** Pill chip naming where a resolved pricing value comes from ("Template", "This menu", …). */
export const PricingSourceBadge = React.forwardRef<HTMLElement, PricingSourceBadgeProps>(
  ({ label, tone = 'gray', interactive = false, className, ...props }, ref) => {
    const classes = cn(
      'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium tracking-tight',
      interactive && 'cursor-pointer',
      sourceToneClasses[tone],
      className,
    )
    if (interactive) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          className={classes}
          {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
          {label}
        </button>
      )
    }
    return (
      <span ref={ref as React.Ref<HTMLSpanElement>} className={classes} {...props}>
        {label}
      </span>
    )
  },
)
PricingSourceBadge.displayName = 'PricingSourceBadge'

// ---------------------------------------------------------------------------
// Margin cell — cash margin and take-rate %, e.g. "$1.50 · 43%".
// ---------------------------------------------------------------------------

export interface PricingMarginCellProps {
  priceCents: number | null
  costCents: number | null
  /** Explicit margin override; derived as price − cost when omitted. */
  marginCents?: number | null
}

/**
 * Margin display: cash margin plus take-rate ((price − cost) / price). The %
 * is omitted when price is 0/null or cost is unknown; a negative margin
 * renders both parts in the destructive tone.
 */
export function PricingMarginCell({ priceCents, costCents, marginCents }: PricingMarginCellProps) {
  const margin = marginCents ?? (priceCents !== null && costCents !== null ? priceCents - costCents : null)
  const takeRate =
    priceCents !== null && priceCents !== 0 && margin !== null ? Math.round((margin / priceCents) * 1000) / 10 : null
  if (margin === null) {
    return <span className="text-sm text-muted-foreground">—</span>
  }
  return (
    <span className={cn('text-sm tabular-nums', margin < 0 && 'font-medium text-destructive')}>
      {formatPriceCents(margin)}
      {takeRate !== null ? (
        <span className={cn('ml-1 text-xs', margin < 0 ? 'text-destructive' : 'text-muted-foreground')}>
          {`· ${takeRate.toFixed(1)}%`}
        </span>
      ) : null}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Matrix types
// ---------------------------------------------------------------------------

export type PricingMatrixCellState = 'inherited' | 'overridden'
export type PricingMatrixField = 'price' | 'cost'

export interface PricingMatrixCell {
  /** Customer price in cents; null = unpriced. */
  price: number | null
  /** Cost (chef pay, vendor pay, …) in cents; rendered when flags.showCost. */
  cost?: number | null
  /** Margin in cents; derived as price − cost when omitted. */
  margin?: number | null
  /** Provenance chip for the resolved price. */
  source: { label: string; tone?: PricingMatrixSourceTone }
  /** Whether the value is inherited from the parent scope or overridden locally. */
  state: PricingMatrixCellState
  /** Collapsed occurrences disagree — inputs get a "Mixed" placeholder. */
  priceMixed?: boolean
  /** Read view shows "Mixed" instead of the em dash when the cost is null. */
  costMixed?: boolean
}

export interface PricingMatrixRowData {
  id: string
  name: string
  cell: PricingMatrixCell
  /** Secondary line under the name (e.g. "Week 2 · Tuesday"). */
  description?: React.ReactNode
  /** Locks this row read-only (and unselectable) regardless of matrix-level flags. */
  readOnly?: boolean
  /** Chip(s) rendered inline after the name. */
  nameBadge?: React.ReactNode
  /** Fine print under the price value/input (e.g. the shadowed base price). */
  priceFootnote?: React.ReactNode
  /** Fine print under the cost value/input (e.g. provenance chip, split-payout note). */
  costFootnote?: React.ReactNode
  /** Extra control rendered inline next to the cost value while editing. */
  costInlineAction?: React.ReactNode
  /** Status cell content; an em dash when omitted. */
  status?: React.ReactNode
  /** Row-actions cell content. The column renders only when some row provides actions. */
  actions?: React.ReactNode
  /** Full-width panel row rendered directly below this row (e.g. an inline editor). */
  detail?: React.ReactNode
}

export interface PricingMatrixGroupData {
  key: string
  /** Section header; null renders the group's rows without a header row. */
  label: string | null
  rows: PricingMatrixRowData[]
}

export interface PricingMatrixFlags {
  /** Render the cost + margin columns. Default true. */
  showCost?: boolean
  /** Price inputs render while editing. Default false. */
  canEditPrice?: boolean
  /** Cost inputs render while editing. Default false. */
  canEditCost?: boolean
}

export interface PricingMatrixLabels {
  item?: string
  price?: string
  cost?: string
  margin?: string
  source?: string
  status?: string
}

export interface PricingMatrixProps {
  groups: PricingMatrixGroupData[]
  flags?: PricingMatrixFlags
  /** Edit-session toggle; when false everything renders read-only. Default true. */
  editing?: boolean
  /** Disables inputs and checkboxes (e.g. while a save is in flight). */
  disabled?: boolean
  /**
   * Controlled money-input values by row id — REQUIRED for rows being edited
   * (the inputs are fully controlled; hold the strings in the consumer).
   */
  draft?: Record<string, { price?: string; cost?: string }>
  /**
   * Controlled bulk selection. Providing onSelectedChange renders the
   * selection column while editing; wire your own bulk UI off the set.
   */
  selected?: ReadonlySet<string>
  onSelectedChange?: (next: Set<string>) => void
  /** Cell-edit callback; value is the raw money-input string. */
  onEdit?: (rowId: string, field: PricingMatrixField, value: string) => void
  /** Makes the default source badge a button. Ignored when renderSource is provided. */
  onExplain?: (rowId: string) => void
  /** Wrap or replace the default source badge (e.g. with an explain popover). */
  renderSource?: (row: PricingMatrixRowData, badge: React.ReactNode) => React.ReactNode
  labels?: PricingMatrixLabels
  /** Rendered as a full-width row when every group is empty. */
  emptyState?: React.ReactNode
  className?: string
}

// ---------------------------------------------------------------------------
// Matrix
// ---------------------------------------------------------------------------

const DEFAULT_LABELS: Required<PricingMatrixLabels> = {
  item: 'Item',
  price: 'Price',
  cost: 'Cost',
  margin: 'Margin',
  source: 'Source',
  status: 'Status',
}

/**
 * Presentational pricing table: grouped rows of price / cost / margin /
 * source / status. Purely props-in — no data fetching; editing is fully
 * controlled through `draft` + `onEdit`, bulk selection through `selected` +
 * `onSelectedChange`, and provenance drill-downs through `onExplain` /
 * `renderSource`. Enter in a money input advances to the next row's input
 * for the same field.
 */
export function PricingMatrix({
  groups,
  flags,
  editing = true,
  disabled = false,
  draft,
  selected,
  onSelectedChange,
  onEdit,
  onExplain,
  renderSource,
  labels,
  emptyState,
  className,
}: PricingMatrixProps) {
  const showCost = flags?.showCost !== false
  const canEditPrice = flags?.canEditPrice === true
  const canEditCost = flags?.canEditCost === true
  const columnLabels = { ...DEFAULT_LABELS, ...labels }

  const inputRefs = React.useRef(new Map<string, HTMLInputElement | null>())

  const allRows = React.useMemo(() => groups.flatMap((group) => group.rows), [groups])
  const showSelection = editing && onSelectedChange !== undefined
  const selectableRows = React.useMemo(() => allRows.filter((row) => row.readOnly !== true), [allRows])
  const showActions = editing && allRows.some((row) => row.actions !== undefined)

  const columnCount = (showSelection ? 1 : 0) + 2 + (showCost ? 2 : 0) + 2 + (showActions ? 1 : 0)

  const handleMoneyKeyDown = (field: PricingMatrixField, index: number) => {
    return (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
        return
      }
      event.preventDefault()
      const next = inputRefs.current.get(`${field}:${index + 1}`)
      next?.focus()
      next?.select()
    }
  }

  const toggleSelected = (rowId: string, value: boolean) => {
    if (!onSelectedChange) {
      return
    }
    const next = new Set(selected ?? [])
    if (value) {
      next.add(rowId)
    } else {
      next.delete(rowId)
    }
    onSelectedChange(next)
  }

  const isSelected = (rowId: string) => selected?.has(rowId) === true

  let flatIndex = -1

  return (
    <TableContainer className={className}>
      <Table>
        <TableHeader>
          <TableRow>
            {showSelection ? (
              <TableHead className="w-10">
                <Checkbox
                  checked={
                    selectableRows.length > 0 && selectableRows.every((row) => isSelected(row.id))
                      ? true
                      : selectableRows.some((row) => isSelected(row.id))
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(value) => {
                    if (!onSelectedChange) {
                      return
                    }
                    const next = new Set(selected ?? [])
                    if (value === true) {
                      selectableRows.forEach((row) => next.add(row.id))
                    } else {
                      selectableRows.forEach((row) => next.delete(row.id))
                    }
                    onSelectedChange(next)
                  }}
                  aria-label="Select all visible rows"
                />
              </TableHead>
            ) : null}
            <TableHead>{columnLabels.item}</TableHead>
            <TableHead className="w-36">{columnLabels.price}</TableHead>
            {showCost ? <TableHead className="w-32">{columnLabels.cost}</TableHead> : null}
            {showCost ? <TableHead className="w-32">{columnLabels.margin}</TableHead> : null}
            <TableHead className="w-28">{columnLabels.source}</TableHead>
            <TableHead className="w-32">{columnLabels.status}</TableHead>
            {showActions ? <TableHead className="w-10" /> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {allRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="py-8 text-center text-sm text-muted-foreground">
                {emptyState ?? 'No rows.'}
              </TableCell>
            </TableRow>
          ) : (
            groups.map((group) =>
              group.rows.length === 0 ? null : (
                <React.Fragment key={group.key}>
                  {group.label ? (
                    <TableRow className="hover:bg-muted/40">
                      <TableCell
                        colSpan={columnCount}
                        className="bg-muted/40 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {group.label}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {group.rows.map((row) => {
                    flatIndex += 1
                    return (
                      <PricingMatrixBodyRow
                        key={row.id}
                        row={row}
                        index={flatIndex}
                        columnCount={columnCount}
                        editing={editing}
                        disabled={disabled}
                        showCost={showCost}
                        canEditPrice={canEditPrice}
                        canEditCost={canEditCost}
                        showSelection={showSelection}
                        showActions={showActions}
                        selected={isSelected(row.id)}
                        draftValue={draft?.[row.id]}
                        onToggleSelected={(value) => toggleSelected(row.id, value)}
                        onEdit={onEdit}
                        onExplain={onExplain}
                        renderSource={renderSource}
                        onKeyDown={handleMoneyKeyDown}
                        registerInput={(field, rowIndex, element) => {
                          inputRefs.current.set(`${field}:${rowIndex}`, element)
                        }}
                      />
                    )
                  })}
                </React.Fragment>
              ),
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function PricingMatrixBodyRow({
  row,
  index,
  columnCount,
  editing,
  disabled,
  showCost,
  canEditPrice,
  canEditCost,
  showSelection,
  showActions,
  selected,
  draftValue,
  onToggleSelected,
  onEdit,
  onExplain,
  renderSource,
  onKeyDown,
  registerInput,
}: {
  row: PricingMatrixRowData
  index: number
  columnCount: number
  editing: boolean
  disabled: boolean
  showCost: boolean
  canEditPrice: boolean
  canEditCost: boolean
  showSelection: boolean
  showActions: boolean
  selected: boolean
  draftValue?: { price?: string; cost?: string }
  onToggleSelected: (value: boolean) => void
  onEdit?: (rowId: string, field: PricingMatrixField, value: string) => void
  onExplain?: (rowId: string) => void
  renderSource?: (row: PricingMatrixRowData, badge: React.ReactNode) => React.ReactNode
  onKeyDown: (field: PricingMatrixField, index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => void
  registerInput: (field: PricingMatrixField, index: number, element: HTMLInputElement | null) => void
}) {
  const rowEditable = editing && row.readOnly !== true
  const priceEditable = rowEditable && canEditPrice
  const costEditable = rowEditable && canEditCost
  const priceDraft = draftValue?.price ?? ''
  const costDraft = draftValue?.cost ?? ''

  // Margin follows the live draft for editable fields, the saved cell otherwise.
  const marginPriceCents = priceEditable
    ? priceInputInvalid(priceDraft)
      ? null
      : priceInputToCents(priceDraft)
    : row.cell.price
  const marginCostCents = costEditable
    ? priceInputInvalid(costDraft)
      ? null
      : priceInputToCents(costDraft)
    : (row.cell.cost ?? null)

  const sourceInteractive = renderSource !== undefined || onExplain !== undefined
  const sourceBadge = (
    <PricingSourceBadge
      label={row.cell.source.label}
      tone={row.cell.source.tone ?? 'gray'}
      interactive={sourceInteractive}
      onClick={onExplain ? () => onExplain(row.id) : undefined}
    />
  )

  const costReadValue =
    row.cell.cost === null || row.cell.cost === undefined
      ? row.cell.costMixed
        ? 'Mixed'
        : '—'
      : formatPriceCents(row.cell.cost)
  const costInline = editing && row.readOnly !== true ? row.costInlineAction : undefined

  return (
    <>
      <TableRow data-pricing-state={row.cell.state}>
        {showSelection ? (
          <TableCell>
            {row.readOnly === true ? null : (
              <Checkbox
                checked={selected}
                onCheckedChange={(value) => onToggleSelected(value === true)}
                aria-label={`Select ${row.name}`}
              />
            )}
          </TableCell>
        ) : null}
        <TableCell className="min-w-48">
          <p className="font-medium">
            {row.name}
            {row.nameBadge}
          </p>
          {row.description ? <p className="mt-0.5 text-[11px] text-muted-foreground">{row.description}</p> : null}
        </TableCell>
        <TableCell>
          {priceEditable ? (
            <div>
              <Input
                ref={(element) => registerInput('price', index, element)}
                value={priceDraft}
                inputMode="decimal"
                placeholder={row.cell.priceMixed ? 'Mixed' : '—'}
                disabled={disabled}
                aria-invalid={priceInputInvalid(priceDraft)}
                className={cn('h-8 w-24 tabular-nums', priceInputInvalid(priceDraft) && 'border-destructive')}
                onChange={(event) => onEdit?.(row.id, 'price', event.target.value)}
                onKeyDown={onKeyDown('price', index)}
              />
              {row.priceFootnote}
            </div>
          ) : (
            <>
              <span className="text-sm tabular-nums">
                {row.cell.price === null ? '—' : formatPriceCents(row.cell.price)}
              </span>
              {row.priceFootnote}
            </>
          )}
        </TableCell>
        {showCost ? (
          <TableCell>
            {costEditable || costInline !== undefined ? (
              <div className="flex items-center gap-1">
                {costEditable ? (
                  <Input
                    ref={(element) => registerInput('cost', index, element)}
                    value={costDraft}
                    inputMode="decimal"
                    placeholder={row.cell.costMixed ? 'Mixed' : '—'}
                    disabled={disabled}
                    aria-invalid={priceInputInvalid(costDraft)}
                    className={cn('h-8 w-24 tabular-nums', priceInputInvalid(costDraft) && 'border-destructive')}
                    onChange={(event) => onEdit?.(row.id, 'cost', event.target.value)}
                    onKeyDown={onKeyDown('cost', index)}
                  />
                ) : (
                  <span className="text-sm tabular-nums">{costReadValue}</span>
                )}
                {costInline}
              </div>
            ) : (
              <span className="text-sm tabular-nums">{costReadValue}</span>
            )}
            {row.costFootnote}
          </TableCell>
        ) : null}
        {showCost ? (
          <TableCell>
            <PricingMarginCell
              priceCents={marginPriceCents}
              costCents={marginCostCents}
              marginCents={priceEditable || costEditable ? undefined : row.cell.margin}
            />
          </TableCell>
        ) : null}
        <TableCell>{renderSource ? renderSource(row, sourceBadge) : sourceBadge}</TableCell>
        <TableCell>{row.status ?? <span className="text-xs text-muted-foreground">—</span>}</TableCell>
        {showActions ? <TableCell>{row.actions}</TableCell> : null}
      </TableRow>
      {row.detail !== undefined && row.detail !== null ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={columnCount} className="p-2">
            {row.detail}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  )
}
